// Mock factories for floodway / surcharge / base structure codes.
// Each factory takes a messageKey (the topic key like 'B1', 'B2', etc.)
// and returns a payload shaped like the real /check response:
//   { message_key, data: { global_variables, result: [ { error_id, ... } ] } }
//
// Goal: each builder must populate every field the matching internal/*.js
// component reads off of `row`, `row.data[i]` and `data.global_variables`
// so the component mounts and renders without throwing.

const globalVariables = {
    has_floodway: true,
    floodway_plan: 'FloodwayPlan01',
    base_plan: 'BasePlan01',
    profile: '100yr',
};

// --- B1 (SRC-00 / FW 01 / FW 01 IB) ----------------------------------------

const surchargeRows = () => ([
    {cross_section: 'RS 12345', femafw_prof_delta_ws: 1.25},
    {cross_section: 'RS 12500', femafw_prof_delta_ws: -0.35},
    {cross_section: 'RS 12700', femafw_prof_delta_ws: 1.85},
]);

// --- B2 (FW 02 / ENCR 00) --------------------------------------------------

const encroachmentStationRows = () => ([
    {cross_section: 'RS 12345', side: 'Left', issue: 'Encroachment outside main channel'},
    {cross_section: 'RS 12500', side: 'Right', issue: 'Encroachment within ineffective area'},
    {cross_section: 'RS 12700', side: 'Left', issue: 'Missing encroachment station'},
]);

// --- B3 (FW 03 / EM 00) ----------------------------------------------------

const encroachmentMethodRows = () => ([
    {cross_section: 'RS 12345', method: 3},
    {cross_section: 'RS 12500', method: 4},
    {cross_section: 'RS 12700', method: null},
]);

// --- B4 (RT 01 / RT 02) ----------------------------------------------------

const structureCoefRows = () => ([
    {
        cross_section: 'RS 12345',
        cont: 0.3,
        exp: 0.5,
        number: 1,
        type: 'Bridge',
        culvert: 'RS 12340',
    },
    {
        cross_section: 'RS 12500',
        cont: 0.6,
        exp: 0.8,
        number: 2,
        type: 'Culvert',
        culvert: 'RS 12495',
    },
    {
        cross_section: 'RS 12700',
        cont: 0.1,
        exp: 0.3,
        number: 1,
        type: 'Bridge',
        culvert: 'RS 12695',
    },
]);

const naturalCoefRows = () => ([
    {cross_section: 'RS 12345', cont: 0.1, exp: 0.3},
    {cross_section: 'RS 12500', cont: 0.25, exp: 0.55},
    {cross_section: 'RS 12700', cont: 0.05, exp: 0.15},
]);

// --- RT3 (RT 06 / RT 07 / RT 08) -------------------------------------------

const culvertEntranceExitRows = () => ([
    {cross_section: 'RS 12345', culvert_name: 'Culvert #1', entrance_coeff: 0.5, exit_coeff: 0.8},
    {cross_section: 'RS 12500', culvert_name: 'Culvert #2', entrance_coeff: 0.1, exit_coeff: 1.2},
    {cross_section: 'RS 12700', culvert_name: 'Culvert #3', entrance_coeff: 0.9, exit_coeff: 0.3},
]);

const culvertManningRows = () => ([
    {cross_section: 'RS 12345', culvert_name: 'Culvert #1', mann_top: 0.018, mann_bottom: 0.022},
    {cross_section: 'RS 12500', culvert_name: 'Culvert #2', mann_top: 0.008, mann_bottom: 0.04},
    {cross_section: 'RS 12700', culvert_name: 'Culvert #3', mann_top: 0.025, mann_bottom: 0.03},
]);

const weirCoefRows = () => ([
    {cross_section: 'RS 12345', culvert_name: 'Culvert #1', weir_coef: 3.0},
    {cross_section: 'RS 12500', culvert_name: 'Culvert #2', weir_coef: 2.0},
    {cross_section: 'RS 12700', culvert_name: 'Culvert #3', weir_coef: 4.5},
]);

// --- B5 (ST 01) ------------------------------------------------------------

const structureRatioRows = () => ([
    {
        cross_section: 'RS 12345',
        type: 'Bridge',
        up_dist: 120.0,
        up_cross_section: 'RS 12400',
        down_cross_section: 'RS 12300',
        up_left_ratio: '1.2:1',
        up_left_ratio_skewed: '1.1:1',
        up_left_ratio_in_range: true,
        up_left_diff: 110.0,
        up_left_calc: '1.2:1',
        up_right_ratio: '1.5:1',
        up_right_ratio_skewed: '1.4:1',
        up_right_ratio_in_range: false,
        up_right_diff: 115.0,
        up_right_calc: '1.5:1',
        down_dist: 100.0,
        down_left_diff: 95.0,
        down_left_ratio: '2.0:1',
        down_left_ratio_skewed: '1.9:1',
        down_left_ratio_in_range: true,
        down_left_calc: '2.0:1',
        down_right_diff: 90.0,
        down_right_ratio: '2.5:1',
        down_right_ratio_skewed: '2.4:1',
        down_right_ratio_in_range: false,
        down_right_calc: '2.5:1',
        skew_map: 12.5,
        skew: 12.5,
    },
    {
        cross_section: 'RS 12500',
        type: 'Culvert',
        up_dist: 80.0,
        up_cross_section: 'RS 12550',
        down_cross_section: 'RS 12450',
        up_left_ratio: '1.0:1',
        up_left_ratio_skewed: '0.95:1',
        up_left_ratio_in_range: true,
        up_left_diff: 75.0,
        up_left_calc: '1.0:1',
        up_right_ratio: '1.1:1',
        up_right_ratio_skewed: '1.05:1',
        up_right_ratio_in_range: true,
        up_right_diff: 78.0,
        up_right_calc: '1.1:1',
        down_dist: 70.0,
        down_left_diff: 68.0,
        down_left_ratio: '1.8:1',
        down_left_ratio_skewed: '1.75:1',
        down_left_ratio_in_range: false,
        down_left_calc: '1.8:1',
        down_right_diff: 72.0,
        down_right_ratio: '2.2:1',
        down_right_ratio_skewed: '2.15:1',
        down_right_ratio_in_range: true,
        down_right_calc: '2.2:1',
        skew: 5.25,
    },
]);

const makeResult = (errorId, errorMessage, data) => ({
    message_key: undefined, // overwritten by factory
    data: {
        global_variables: globalVariables,
        result: [{
            error_id: errorId,
            error_message: errorMessage,
            data,
        }],
    },
});

const factory = (errorId, errorMessage, dataBuilder) => (messageKey) => {
    const payload = makeResult(errorId, errorMessage, dataBuilder());
    payload.message_key = messageKey;
    return payload;
};

export const floodwayMocks = {
    'SRC-00': factory(
        'SRC-00',
        'There is no floodway plan defined for the project; floodway surcharge checks cannot be performed.',
        () => [{}],
    ),
    'FW 01': factory(
        'FW 01',
        'Encroachment surcharges out of range at the listed cross sections.',
        surchargeRows,
    ),
    'FW 01 IB': factory(
        'FW 01 IB',
        'Encroachment surcharges out of range at the listed internal bridge cross sections.',
        surchargeRows,
    ),
    'FW 02': factory(
        'FW 02',
        'Issues with encroachment station placement at the listed cross sections.',
        encroachmentStationRows,
    ),
    'ENCR 00': factory(
        'ENCR 00',
        'There are no encroachment results available for the floodway plan.',
        () => [{}],
    ),
    'FW 03': factory(
        'FW 03',
        'Encroachment Method 1 is not used at the listed cross sections.',
        encroachmentMethodRows,
    ),
    'EM 00': factory(
        'EM 00',
        'No encroachment method information could be loaded for the floodway plan.',
        () => [{}],
    ),
    'RT 01': factory(
        'RT 01',
        'Contraction / expansion coefficients differ from the recommended values at the listed structure cross sections.',
        structureCoefRows,
    ),
    'RT 02': factory(
        'RT 02',
        'Contraction / expansion coefficients differ from the recommended values at the listed natural cross sections.',
        naturalCoefRows,
    ),
    'RT 06': factory(
        'RT 06',
        'Culvert entrance or exit loss coefficient is outside the recommended range at the listed culverts.',
        culvertEntranceExitRows,
    ),
    'RT 07': factory(
        'RT 07',
        "Culvert Manning's n value is outside the recommended range at the listed culverts.",
        culvertManningRows,
    ),
    'RT 08': factory(
        'RT 08',
        'Weir flow coefficient is outside the recommended range at the listed culverts.',
        weirCoefRows,
    ),
    'ST 01': factory(
        'ST 01',
        'Contraction / expansion ratios at the upstream / downstream of structures are out of tolerance.',
        structureRatioRows,
    ),
};
